'use client';

import { FC, PropsWithChildren, useMemo } from 'react';

import classNames from 'classnames';

import { useAriaErrorMessageId } from '../../../../hooks/useAriaErrorMessageId/useAriaErrorMessageId';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';

import RadioGroupContext from './RadioGroupContext';

type BoxSpacing = 'none' | '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

interface Props extends PropsWithChildren {
   name?: string;
   gap?: BoxSpacing;
   value?: any;
   errorMessage?: string;
   optionsLayout?: 'vertical' | 'horizontal';
   onChange?: (value: any) => void;
}

const gapClassNames = {
   '2xl': 'gap-component-layout-box-spacing-gap-2xl',
   '2xs': 'gap-component-layout-box-spacing-gap-2xs',
   '3xl': 'gap-component-layout-box-spacing-gap-3xl',
   '3xs': 'gap-component-layout-box-spacing-gap-3xs',
   lg: 'gap-component-layout-box-spacing-gap-lg',
   md: 'gap-component-layout-box-spacing-gap-md',
   none: 'gap-component-layout-box-spacing-gap-none',
   sm: 'gap-component-layout-box-spacing-gap-sm',
   xl: 'gap-component-layout-box-spacing-gap-xl',
   xs: 'gap-component-layout-box-spacing-gap-xs'
};

export const RadioGroup: FC<Props> = ({ name, gap = 'none', value, errorMessage, optionsLayout = 'vertical', onChange, children }) => {
   const errorMessageId = useAriaErrorMessageId(!!errorMessage, 'radio-group');

   const contextValue = useMemo(
      () => ({
         name,
         onChange: (newValue: string) => {
            onChange?.(newValue);
         },
         value
      }),
      [name, onChange, value]
   );

   return (
      <fieldset data-testid="radio-group" aria-describedby={errorMessageId} className={classNames('inline-flex', optionsLayout === 'vertical' ? 'flex-col' : 'flex-row flex-wrap', gapClassNames[gap])}>
         <RadioGroupContext value={contextValue}>{children}</RadioGroupContext>

         {errorMessage && (
            <div role="alert" id={errorMessageId} className="pl-component-input-radio-group-supporting-text-spacing-padding-left gap-component-input-radio-group-supporting-text-spacing-gap text-component-input-radio-group-supporting-text-color-text-error flex items-center">
               <Icon icon="exclamation-circle" size="caption" color="inherit" />
               <Typography variant="caption" color="inherit">
                  {errorMessage}
               </Typography>
            </div>
         )}
      </fieldset>
   );
};
